import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getIcon, SLRSpinner } from '@slr/shared-library';
import { INVITATION_HANDLE, PROFILE_PATH } from 'configs';
import {
  useFindMembershipsOfUser,
  useGetCurrentUserProfileWithSolution1,
  useGetOrganization
} from 'hooks';
import { userNeedsRegistrationStepTwo } from 'hooks/profile/userNeedsRegistrationStepTwo';
import { useQueryParam } from 'providers';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import {
  buildInvitationSearch,
  resolvePendingInvitation
} from 'utils/pendingInvitation';
import { AddressForm } from './address/AddressForm';

import './CompleteRegistrationPage.scss';

export const CompleteRegistrationPage: FC = () => {
  const { t } = useTranslation('translation');
  const navigate = useNavigate();
  const location = useLocation();
  const { invitationQuery } = useQueryParam();

  const pendingInvitation = useMemo(
    () =>
      resolvePendingInvitation(location.search, invitationQuery, {
        includeStored: false
      }),
    [location.search, invitationQuery]
  );

  const { data: invitedOrganization } = useGetOrganization(
    pendingInvitation?.organizationId ?? ''
  );

  const { data: userProfile, isPending: isProfileLoading } =
    useGetCurrentUserProfileWithSolution1();

  const { data: memberships, isPending: isMembershipsLoading } =
    useFindMembershipsOfUser();

  const isLoading = isProfileLoading || isMembershipsLoading;

  useEffect(() => {
    if (isLoading) return;

    if (userNeedsRegistrationStepTwo(userProfile, memberships)) {
      return;
    }

    if (pendingInvitation?.token && pendingInvitation.organizationId) {
      navigate(
        `${INVITATION_HANDLE}${buildInvitationSearch(pendingInvitation, {
          acceptImmediately: true
        })}`,
        { replace: true }
      );
      return;
    }
    navigate(PROFILE_PATH, { replace: true });
  }, [
    userProfile,
    memberships,
    isLoading,
    navigate,
    pendingInvitation,
  ]);

  const handleAddressSuccess = () => {
    if (pendingInvitation?.token && pendingInvitation.organizationId) {
      navigate(
        `${INVITATION_HANDLE}${buildInvitationSearch(pendingInvitation, {
          acceptImmediately: true
        })}`
      );
      return;
    }
    navigate(PROFILE_PATH);
  };

  if (isLoading) return <SLRSpinner />;

  return (
    <div className="profile-page register-page">
      <div className="py-6 register-wrapper">
        <div className="register-card">
          <div className="register-steps">
            <div className="register-step register-step--completed">
              <div className="register-step__title">
                <span>{t('profilePage.steps.step1.title')}</span>
                <FontAwesomeIcon
                  className="success-icon"
                  icon={getIcon('fas', 'check')}
                />
              </div>
              <p>{t('profilePage.steps.step1.description')}</p>
            </div>
            <div className="register-step register-step--current">
              <div className="register-step__title">
                <span>{t('profilePage.steps.step2.title')}</span>
              </div>
              <p>{t('profilePage.steps.step2.description')}</p>
            </div>
          </div>
          <div className="register-card-body">
            {invitedOrganization?.name && (
              <p className="text-muted mb-4">
                {t('invitationsPage.completeRegistration.hint', {
                  organizationName: invitedOrganization.name
                })}
              </p>
            )}
            <AddressForm
              onSuccess={handleAddressSuccess}
              isRegistration
              hasPendingInvitation={
                !!(
                  pendingInvitation?.token && pendingInvitation.organizationId
                )
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};
